// Extracts the first "previewable" http(s) URL from composer draft text.
//
// A previewable URL is a plain http/https web link. We deliberately ignore
// Delta Chat's own scheme links (invite codes, OpenPGP fingerprints, mailto,
// etc.) because generating a web preview for them makes no sense and could be
// privacy-surprising.

const URL_RE = /\bhttps?:\/\/[^\s<>"'`]+/gi

// Schemes/hosts we never preview even if they appear as an http(s) URL.
const IGNORED_HOST_SUFFIXES = ['localhost', '127.0.0.1', '[::1]']

/**
 * Trailing characters that are almost never part of a URL when a link is typed
 * inline in prose (closing brackets, sentence punctuation).
 */
function trimTrailingPunctuation(url: string): string {
  let out = url
  // Strip trailing punctuation, but keep a closing paren if there's a matching
  // opening one inside the URL (e.g. Wikipedia "..._(disambiguation)").
  // eslint-disable-next-line no-constant-condition
  while (out.length > 0) {
    const last = out[out.length - 1]
    if (last === undefined) {
      break
    }
    if (last === ')') {
      const opens = (out.match(/\(/g) || []).length
      const closes = (out.match(/\)/g) || []).length
      if (closes > opens) {
        out = out.slice(0, -1)
        continue
      }
      break
    }
    if ('.,;:!?"\'`>]}'.includes(last)) {
      out = out.slice(0, -1)
      continue
    }
    break
  }
  return out
}

export type DetectedUrl = {
  /** The normalized URL string, safe to pass to the fetcher. */
  url: string
  /** Lower-cased hostname without a leading `www.`. */
  host: string
}

/**
 * Returns the first previewable URL in `text`, or `null` if there is none.
 * Only the first match is considered — we preview at most one link per message.
 */
export function detectPreviewableUrl(text: string): DetectedUrl | null {
  if (!text) {
    return null
  }
  URL_RE.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = URL_RE.exec(text)) !== null) {
    const candidate = trimTrailingPunctuation(match[0])
    let parsed: URL
    try {
      parsed = new URL(candidate)
    } catch {
      continue
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      continue
    }
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '')
    if (!host || IGNORED_HOST_SUFFIXES.some(h => parsed.hostname === h)) {
      continue
    }
    return { url: parsed.toString(), host }
  }
  return null
}
